import React, { useEffect, useState, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { FilterAccordion } from './FilterAccordion';
import '../styles/components/searchModal.css';

export type StockFilter = 'off' | 'low' | 'out';

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  selectedCategory: string;
  onCategoryChange: (value: string) => void;
  categories: string[];
  selectedCategoryIds?: number[];
  onCategoryIdsChange?: (ids: number[]) => void;
  allCategories?: Array<{id: number, key: string, label: string, parent_key?: string}>;
  onClear: () => void;
  stockFilter?: StockFilter;
  onStockFilterChange?: (filter: StockFilter) => void;
  isStockMode?: boolean;
  // New props for location filtering
  items?: Array<{area: string, location: string}>;
  selectedAreas?: string[];
  onAreasChange?: (areas: string[]) => void;
  selectedLocations?: string[];
  onLocationsChange?: (locations: string[]) => void;
  selectedDate?: string; // YYYY-MM-DD
  onDateChange?: (date: string) => void;
  dateFieldType?: 'created' | 'updated' | 'purchased';
  onDateFieldTypeChange?: (type: 'created' | 'updated' | 'purchased') => void;
}

const SearchModal: React.FC<SearchModalProps> = ({
  isOpen,
  onClose,
  searchTerm,
  onSearchChange,
  selectedCategory,
  onCategoryChange,
  categories,
  selectedCategoryIds = [],
  onCategoryIdsChange,
  allCategories = [],
  onClear,
  stockFilter = 'off',
  onStockFilterChange,
  isStockMode = false,
  items = [],
  selectedAreas = [],
  onAreasChange,
  selectedLocations = [],
  onLocationsChange,
  selectedDate,
  onDateChange,
  dateFieldType = 'created',
  onDateFieldTypeChange,
}) => {
  // Accordion state
  const [expandedSections, setExpandedSections] = useState<{[key: string]: boolean}>({
    categories: false,
    location: false,
    stock: false
  });
  
  // Expanded parent categories/areas
  const [expandedParents, setExpandedParents] = useState<Set<string>>(new Set());
  
  // Build location hierarchy from items
  const locationHierarchy = useMemo(() => {
    const hierarchy: {[area: string]: Set<string>} = {};
    items.forEach(item => {
      if (item.area) {
        if (!hierarchy[item.area]) {
          hierarchy[item.area] = new Set();
        }
        if (item.location) {
          hierarchy[item.area].add(item.location);
        }
      }
    });
    return hierarchy;
  }, [items]);
  
  // Organize categories hierarchically
  const categoryHierarchy = useMemo(() => {
    const parents = allCategories.filter(c => !c.parent_key).sort((a, b) => a.label.localeCompare(b.label));
    const children = allCategories.filter(c => c.parent_key);
    
    return parents.map(parent => ({
      ...parent,
      children: children.filter(c => c.parent_key === parent.key).sort((a, b) => a.label.localeCompare(b.label))
    }));
  }, [allCategories]);
  
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  
  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  
  const toggleParent = (parentKey: string) => {
    setExpandedParents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(parentKey)) {
        newSet.delete(parentKey);
      } else {
        newSet.add(parentKey);
      }
      return newSet;
    });
  };
  
  // Category selection handlers
  const handleParentCategoryClick = (parent: any) => {
    if (!onCategoryIdsChange) return;
    
    const childIds = parent.children.map((c: any) => c.id);
    const allSelected = childIds.every((id: number) => selectedCategoryIds.includes(id));
    
    if (allSelected) {
      // Deselect all children
      onCategoryIdsChange(selectedCategoryIds.filter(id => !childIds.includes(id)));
    } else {
      // Select all children
      const newIds = [...selectedCategoryIds];
      childIds.forEach((id: number) => {
        if (!newIds.includes(id)) {
          newIds.push(id);
        }
      });
      onCategoryIdsChange(newIds);
    }
  };
  
  const handleChildCategoryClick = (categoryId: number) => {
    if (!onCategoryIdsChange) return;
    
    if (selectedCategoryIds.includes(categoryId)) {
      onCategoryIdsChange(selectedCategoryIds.filter(id => id !== categoryId));
    } else {
      onCategoryIdsChange([...selectedCategoryIds, categoryId]);
    }
  };
  
  // Location selection handlers
  const handleAreaClick = (area: string) => {
    if (!onAreasChange || !onLocationsChange) return;
    
    const locationsInArea = Array.from(locationHierarchy[area] || []);
    const allLocationsSelected = locationsInArea.every(loc => 
      selectedLocations.includes(`${area}:${loc}`)
    );
    
    if (allLocationsSelected) {
      // Deselect area and all its locations
      onAreasChange(selectedAreas.filter(a => a !== area));
      onLocationsChange(selectedLocations.filter(loc => !loc.startsWith(`${area}:`)));
    } else {
      // Select area and all its locations
      if (!selectedAreas.includes(area)) {
        onAreasChange([...selectedAreas, area]);
      }
      const newLocations = [...selectedLocations];
      locationsInArea.forEach(loc => {
        const locKey = `${area}:${loc}`;
        if (!newLocations.includes(locKey)) {
          newLocations.push(locKey);
        }
      });
      onLocationsChange(newLocations);
    }
  };
  
  const handleLocationClick = (area: string, location: string) => {
    if (!onLocationsChange) return;
    
    const locKey = `${area}:${location}`;
    if (selectedLocations.includes(locKey)) {
      onLocationsChange(selectedLocations.filter(l => l !== locKey));
    } else {
      onLocationsChange([...selectedLocations, locKey]);
    }
  };
  
  // Calculate selection states
  const getCategorySelectionState = (parent: any) => {
    const childIds = parent.children.map((c: any) => c.id);
    const selectedCount = childIds.filter((id: number) => selectedCategoryIds.includes(id)).length;
    
    if (selectedCount === 0) return 'none';
    if (selectedCount === childIds.length) return 'all';
    return 'partial';
  };
  
  const getAreaSelectionState = (area: string) => {
    const locations = Array.from(locationHierarchy[area] || []);
    const selectedCount = locations.filter(loc => 
      selectedLocations.includes(`${area}:${loc}`)
    ).length;
    
    if (selectedCount === 0) return 'none';
    if (selectedCount === locations.length) return 'all';
    return 'partial';
  };

  if (!isOpen) return null;

  const modalContent = (
    <div 
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        zIndex: 20000,
        padding: 0
      }}
    >
      <div 
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'relative',
          width: '100vw',
          maxWidth: '100vw',
          maxHeight: '85vh',
          minHeight: '50vh',
          borderTopLeftRadius: '16px',
          borderTopRightRadius: '16px',
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
          paddingBottom: 'env(safe-area-inset-bottom)',
          boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.1)',
          margin: 0,
          background: 'var(--color-bg-primary)',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Handle bar */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          padding: '8px 0 4px 0'
        }}>
          <div style={{
            width: '36px',
            height: '5px',
            backgroundColor: 'var(--color-border-strong)',
            borderRadius: '3px'
          }} />
        </div>

        <div className="modal-header">
          <h3>Search & Filter</h3>
        </div>

        <div className="modal-body">
          {/* Search Input */}
          <div className="search-section">
            <div className="search-input-wrapper">
              <svg className="search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="M21 21l-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                className="search-input input-standard"
                placeholder="name, #category, @location, ~description"
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                style={{ paddingLeft: '35px' }}
              />
              {searchTerm && (
                <button className="search-clear-btn" onClick={() => onSearchChange('')}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Date Filter */}
          {onDateChange && (
            <div className="search-section">
              <label className="filter-label" style={{ display: 'block', marginBottom: 8 }}>
                Date
              </label>
              
              {/* Date field type selector */}
              {onDateFieldTypeChange && (
                <div style={{ display: 'flex', gap: '16px', marginBottom: 8, flexWrap: 'wrap' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px' }}>
                    <input
                      type="radio"
                      name="dateFieldType"
                      value="created"
                      checked={dateFieldType === 'created'}
                      onChange={(e) => onDateFieldTypeChange(e.target.value as 'created' | 'updated' | 'purchased')}
                      style={{ cursor: 'pointer' }}
                    />
                    Created
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px' }}>
                    <input
                      type="radio"
                      name="dateFieldType"
                      value="updated"
                      checked={dateFieldType === 'updated'}
                      onChange={(e) => onDateFieldTypeChange(e.target.value as 'created' | 'updated' | 'purchased')}
                      style={{ cursor: 'pointer' }}
                    />
                    Updated
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px' }}>
                    <input
                      type="radio"
                      name="dateFieldType"
                      value="purchased"
                      checked={dateFieldType === 'purchased'}
                      onChange={(e) => onDateFieldTypeChange(e.target.value as 'created' | 'updated' | 'purchased')}
                      style={{ cursor: 'pointer' }}
                    />
                    Purchased
                  </label>
                </div>
              )}
              
              <input
                type="date"
                className="input-standard"
                value={selectedDate || ''}
                onChange={(e) => onDateChange(e.target.value)}
              />
            </div>
          )}

          {/* Categories Accordion */}
          {allCategories.length > 0 && onCategoryIdsChange && (
            <FilterAccordion
              title="Categories"
              isExpanded={expandedSections.categories}
              onToggle={() => toggleSection('categories')}
              selectedCount={selectedCategoryIds.length}
            >
              {categoryHierarchy.map(parent => {
                const selectionState = getCategorySelectionState(parent);
                const isExpanded = expandedParents.has(parent.key);
                
                return (
                  <div key={parent.id} className="filter-item-group">
                    <div className="filter-item filter-parent">
                      <button
                        type="button"
                        className="filter-expand-btn"
                        onClick={() => toggleParent(parent.key)}
                      >
                        <svg
                          className={isExpanded ? 'expanded' : ''}
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <polyline points="9 18 15 12 9 6"></polyline>
                        </svg>
                      </button>
                      <div
                        className={`filter-checkbox ${selectionState === 'all' ? 'checked' : ''} ${selectionState === 'partial' ? 'indeterminate' : ''}`}
                        onClick={() => handleParentCategoryClick(parent)}
                      >
                        {selectionState === 'all' && (
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        )}
                      </div>
                      <span className="filter-item-label" onClick={() => handleParentCategoryClick(parent)}>
                        {parent.label}
                      </span>
                      <span className="filter-item-count">
                        {parent.children.length}
                      </span>
                    </div>
                    
                    {isExpanded && parent.children.length > 0 && (
                      <div className="filter-children">
                        {parent.children.map((child: any) => {
                          const isSelected = selectedCategoryIds.includes(child.id);
                          return (
                            <div key={child.id} className="filter-child">
                              <div
                                className={`filter-checkbox ${isSelected ? 'checked' : ''}`}
                                onClick={() => handleChildCategoryClick(child.id)}
                              >
                                {isSelected && (
                                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                    <polyline points="20 6 9 17 4 12"></polyline>
                                  </svg>
                                )}
                              </div>
                              <span className="filter-item-label" onClick={() => handleChildCategoryClick(child.id)}>
                                {child.label}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </FilterAccordion>
          )}


          {/* Stock Filter Accordion - Only show in stock mode */}
          {isStockMode && (
            <FilterAccordion
              title="Stock Status"
              isExpanded={expandedSections.stock}
              onToggle={() => toggleSection('stock')}
              selectedCount={stockFilter !== 'off' ? 1 : 0}
            >
              <div style={{ padding: '8px 0' }}>
                <select
                  className="select-standard"
                  value={stockFilter}
                  onChange={(e) => onStockFilterChange?.(e.target.value as StockFilter)}
                  style={{ width: '100%' }}
                >
                  <option value="off">All Items</option>
                  <option value="low">Low Stock (qty ≤ min qty)</option>
                  <option value="out">Out of Stock (qty = 0)</option>
                </select>
              </div>
            </FilterAccordion>
          )}
        </div>

        <div className="modal-footer">
          <button className="modal-btn-secondary" onClick={onClear}>
            Clear
          </button>
          <button className="modal-btn-primary" onClick={onClose}>
            OK
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default SearchModal;
